import SellersApiV2 from 'api/v2/admins/sellers-api-v2'
import UsersApiV2 from 'api/v2/admins/users-api-v2'
import React, { useEffect, useState } from 'react'
import SellerBadge from 'domains/seller/atoms/seller-badge'
import PropTypes from 'prop-types'
import { useField } from 'formik'
import SelectField from 'components/molecules/select-field'

const propTypes = {
  name               : PropTypes.string,
  label              : PropTypes.string,
  required           : PropTypes.bool,
  searchByUserDetail : PropTypes.bool
}

/**
 * Search field to select seller by email / name / seller id.
 * @param {string} name - Formik field name
 * @param {string} label - Field Label
 * @param {bool} required - It will show required badge if it set `true`
 */
const SellerSelectSearchField = ({
  name,
  label,
  required,
  searchByUserDetail = false,
  ...props
}) => {
  const [ field ]                           = useField(name)
  const [ initialOption, setInitialOption ] = useState(undefined)

  const { value } = field

  /**
   * Custom handler to filter options
   * @param {string} keyword - Input value from the field. Can be seller email / name / id
   * @returns {array} list of sellers that already transform into react-select standart option
   */
  const onFilterOptions = async (keyword) => {
    try {
      if (searchByUserDetail) {
        const response = await UsersApiV2.get({
          additionalFilters: {
            q        : keyword,
            per_page : 25
          }
        })
        const userDetails = response.data.users || []

        // This endpoint returns one row per matching UserDetail, exactly like Create Deal.
        // Keep each option unique for react-select while formValue remains the parent User ID.
        if (userDetails.length > 0) {
          return userDetails.map((seller) => formatOption(seller, true))
        }
      }

      const additionalFilters = {
        code: keyword
      }
      const response = await SellersApiV2.get({ additionalFilters: additionalFilters })
      const sellers  = response.data.sellers

      // if there's no seller, return null
      // so the 'no options' message will be displayed
      if (sellers.length === 0) return null

      return sellers.map((seller) => formatOption(seller))
    }
    catch (error) {
      alert('Something wrong')
    }
  }

  /** Format the option structure to follow react-select convention */
  const formatOption = (seller, detailLevel = false) => ({
    value          : detailLevel && seller.user_detail_id ? `${seller.id}:${seller.user_detail_id}` : seller.id,
    formValue      : seller.id,
    id             : seller.id,
    user_detail_id : seller.user_detail_id,
    label          : seller.name,
    type           : seller.seller_type,
    code           : seller.code || (seller.id ? `CSR-${String(seller.id).padStart(5, '0')}` : ''),
    email          : seller.email,
    phone          : seller.full_phone || seller.phone
  })

  /**
   * Create a custom style options
   * @param {object} option - option
   *  */
  const formatOptionLabel = ({ code, label, type, email, phone, user_detail_id }) => (
    <>
      <span className='mr-3'>{ code }</span>
      <span className='mr-3'>{ label }</span>
      <span className='mr-3 text-muted'>{ email }</span>
      { phone && <span className='mr-3 text-muted'>{ phone }</span> }
      { user_detail_id && <span className='mr-3 text-muted'>{ `Detail #${user_detail_id}` }</span> }
      { type && <SellerBadge className={ 'badge-md' } seller_type={ type } /> }
    </>
  )


  useEffect(() => {
    // Call this block only once when value has filled at the first time
    // show the value first before updating the field
    if (!initialOption && value) {
      SellersApiV2.show(value)
        .then((response) => setInitialOption(formatOption(response.data.seller)))
        .catch(() => alert('Something wrong when showing Seller'))
    }

  }, [ initialOption, value ])

  return (
    <SelectField
      { ...props }
      name={ name }
      label={ label }
      required={ required }
      placeholder={ 'Type seller email / name / ID' }
      defaultValue={ initialOption }
      onFilterOptions={ onFilterOptions }
      formatOptionLabel={ formatOptionLabel }
    />
  )
}

SellerSelectSearchField.propTypes = propTypes

export default SellerSelectSearchField
